import React, { useEffect, useRef } from 'react';
import cn from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { createPortal } from 'react-dom';

import CloseIcon from '../../../../assets/icons/common/CloseIcon';
import SupportIcon from '../../../../assets/icons/common/SupportIcon';
import Button from '../Button';
import { openGladlyChat } from '../../../../utils/gladly';

import styles from './styles.module.scss';

/**
 * Custom Modal component, that renders modal in the portal
 *
 * @param {Boolean} isOpen is modal opened
 * @param {Function} onClose called on modal close
 * @param {Boolean} hideCloseIcon hides close icon in the top right corner
 * @param {Boolean} preventClosing prevents modal from closing using ESC key or clicking on the backdrop
 * @param {Boolean} showSupportFooter show footer in modal with contact support information
 * @param {String} className className prop for modal
 * @returns render modal with content
 */
const Modal = ({
  isOpen,
  onClose = () => {},
  hideCloseIcon = false,
  preventClosing = false,
  showSupportFooter = false,
  className,
  children,
}) => {
  const backdropRef = useRef();

  const handleEscape = (event) => {
    if (!preventClosing && event.keyCode === 27 && isOpen) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (!preventClosing && e.target === backdropRef.current) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    } else {
      document.removeEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
    // eslint-disable-next-line
  }, [isOpen]);

  return createPortal(
    <CSSTransition
      in={isOpen}
      timeout={300}
      classNames={{
        enterActive: styles.animationEnterActive,
        enter: styles.animationEnter,
        exit: styles.animationExit,
        exitActive: styles.animationExitActive,
      }}
      nodeRef={backdropRef}
      unmountOnExit
    >
      <div
        ref={backdropRef}
        className={styles.backdrop}
        onClick={handleBackdropClick}
      >
        <div
          className={cn(styles.modal, className, {
            [styles.withSupportFooter]: showSupportFooter,
          })}
        >
          {!hideCloseIcon && (
            <button
              className={styles.closeButton}
              data-cy={'close-btn'}
              onClick={onClose}
            >
              <CloseIcon className={styles.closeIcon} />
            </button>
          )}
          {children}
          {showSupportFooter && (
            <div className={styles.supportFooter}>
              <span>
                Got questions or need to talk it over? We’re right here!
              </span>
              <Button onClick={openGladlyChat} colorType="white" size="small">
                <SupportIcon />
                Ask Support
              </Button>
            </div>
          )}
        </div>
      </div>
    </CSSTransition>,
    document.getElementById('modal-container'),
  );
};

export default Modal;
